<template>
    <div>
        <label v-if="label" class="block text-sm font-medium leading-6 text-gray-900" v-text="label"/>
        <div
            class="mt-2 flex justify-center rounded-lg border border-dashed px-6 py-6"
            :class="active ? 'border-gray-900/50' : 'border-gray-900/25'"
            @dragenter.prevent="setActive"
            @dragover.prevent="setActive"
            @dragleave.prevent="setInactive"
            @drop.prevent="onDrop"
        >
            <div>
                <div v-if="files.length !== maxFiles" class="text-center">
                    <QrIcon class="hidden md:block mx-auto h-12 w-12 text-gray-300" icon="image"/>
                    <div class="mt-4 flex justify-center text-sm leading-6 text-gray-600">
                        <label
                            for="file-upload"
                            class="relative cursor-pointer rounded-md bg-white font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500"
                        >
                            <span v-if="maxFiles === 1" v-text="trans('form.select-a-file')"/>
                            <span v-else v-text="trans('form.select-files')"/>
                            <input
                                id="file-upload"
                                name="file-upload"
                                type="file"
                                class="sr-only"
                                @change="onInputChange"
                                :multiple="maxFiles > 1"
                            />
                        </label>
                        <p class="pl-1" v-text="trans('form.or-drag-and-drop')"/>
                    </div>
                    <p class="text-xs leading-5 text-gray-600" v-text="helperText"/>
                </div>

                <ul
                    v-show="files.length"
                    class="mt-4 grid gap-4 mx-auto"
                    :class="maxFiles === 1 ? 'grid-cols-1' : 'grid-cols-4 md:grid-cols-5'"
                >
                    <QrFileUploadPreview
                        v-for="file in files"
                        :key="file.id"
                        :file="file"
                        :single="maxFiles === 1"
                        @remove="removeFile"
                    />
                </ul>

                <div class="mt-4 flex">
                    <QrButton
                        v-if="uploaded"
                        class="mx-auto"
                        @click.prevent="removeFiles"
                        :text="trans('button.reset')"
                        trans="button.reset"
                    />
                    <QrButton
                        v-else-if="files.length"
                        class="mx-auto"
                        positive
                        icon-right="arrow-up-to-arc"
                        @click.prevent="uploadFiles(files)"
                        :text="trans('button.upload')"
                        trans="button.upload"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import QrIcon from "@/components/application-ui/elements/QrIcon.vue";
import { trans } from "@/libs/helper.js";
import {computed, onMounted, onUnmounted, ref} from 'vue';
import useFileList from "@/libs/file-list.js";
import QrFileUploadPreview from "@/components/application-ui/form/QrFileUploadPreview.vue";
import QrButton from "@/components/application-ui/elements/QrButton.vue";

const emit = defineEmits(['files-dropped']);
const props = defineProps({
    label: String,
    helperText: {
        required: true,
        type: String
    },
    maxFiles: {
        type: Number,
        default: 1
    },
    errors: {
        required: false,
        type: Array
    },
    uploadFiles: Function,
    directUpload: Boolean,
});

onMounted(() => {
    events.forEach((eventName) => {
        document.body.addEventListener(eventName, preventDefaults)
    });
});

onUnmounted(() => {
    events.forEach((eventName) => {
        document.body.removeEventListener(eventName, preventDefaults)
    });
});

const { files, addFiles, removeFile, removeFiles } = useFileList();
const events = ['dragenter', 'dragover', 'dragleave', 'drop'];
const active = ref(false);
let inActiveTimeout = null;

const setActive = () => {
    active.value = true
    clearTimeout(inActiveTimeout)
};

const setInactive = () => {
    inActiveTimeout = setTimeout(() => {
        active.value = false
    }, 50)
};

const onDrop = (e) => {
    active.value = false;
    const filesToAdd = Array.prototype.slice.call(e.dataTransfer.files, 0, props.maxFiles);
    addFiles([...filesToAdd]);

    if (props.directUpload) {
        props.uploadFiles(files.value);
    }
};

const preventDefaults = (e) => {
    e.preventDefault();
};

const onInputChange = (e) => {
    addFiles(e.target.files);
    e.target.value = null;

    if (props.directUpload) {
        props.uploadFiles(files.value);
    }
};

const uploaded = computed(() => {
    return !! files.value.find(file => file.status === 'success')
});

</script>
